import type * as Yup from 'yup';

// Wire-protocol action strings. The WS layer knows these identifiers; feature
// code provides payload schemas.
export const INBOUND_WS_ACTIONS = [
  'receiving_session_updated',
  'companion_status_changed',
  'session_lease_released',
  'store_updated',
] as const;

export type InboundWsAction = (typeof INBOUND_WS_ACTIONS)[number];

// Outbound action strings. `ping` is NOT in this enum: it is internal to the
// provider's keepalive and does not go through the public send API.
export type OutboundWsAction = 'register_tab';

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

// Result of a synchronous send call. `dropped_closed` is expected during
// reconnect; `dropped_invalid` signals a programming error (schema mismatch).
export type SendResult = 'sent' | 'dropped_closed' | 'dropped_invalid';

// Type-level guardrail: the feature-provided schema must NOT define an
// `action` key. The WS layer owns the action field; payloads carry only
// domain fields. When a consumer's schema resolves to an object whose
// `action` key exists, this type resolves to an error object no real schema
// can satisfy, producing a compile error with the `__WS_SCHEMA_ERROR__`
// marker in the message.
export type ForbidActionKey<T> = 'action' extends keyof T
  ? {
      __WS_SCHEMA_ERROR__: "Payload schema must not define 'action' — the WS layer owns the action field. Your schema should contain only payload fields.";
    }
  : unknown;

export type Subscribe = <S extends Yup.ObjectSchema<Yup.AnyObject>>(
  action: InboundWsAction,
  payloadSchema: S & ForbidActionKey<Yup.InferType<S>>,
  handler: (payload: Yup.InferType<S>) => void
) => () => void;

export type Send = <S extends Yup.ObjectSchema<Yup.AnyObject>>(
  action: OutboundWsAction,
  payloadSchema: S & ForbidActionKey<Yup.InferType<S>>,
  payload: Yup.InferType<S>
) => SendResult;
